<template>
  <div class="skeleton-loader">
    <!-- Table Skeleton -->
    <div v-if="type === 'table'" class="skeleton-table">
      <div class="skeleton-table-header">
        <div v-for="n in columns" :key="n" class="skeleton-cell skeleton-header-cell"></div>
      </div>
      <div v-for="row in rows" :key="row" class="skeleton-table-row">
        <div v-for="n in columns" :key="n" class="skeleton-cell">
          <div class="skeleton-line" :style="{ width: getCellWidth(n) }"></div>
        </div>
      </div>
    </div>

    <!-- Card Skeleton -->
    <div v-else-if="type === 'card'" class="skeleton-card">
      <div v-for="card in count" :key="card" class="skeleton-card-item">
        <div class="skeleton-avatar"></div>
        <div class="skeleton-content">
          <div class="skeleton-line skeleton-title"></div>
          <div class="skeleton-line skeleton-subtitle"></div>
          <div class="skeleton-line skeleton-text" style="width: 60%;"></div>
        </div>
      </div>
    </div>

    <!-- List Skeleton -->
    <div v-else-if="type === 'list'" class="skeleton-list">
      <div v-for="item in count" :key="item" class="skeleton-list-item">
        <div class="skeleton-circle"></div>
        <div class="skeleton-content">
          <div class="skeleton-line skeleton-list-title"></div>
          <div class="skeleton-line skeleton-list-subtitle"></div>
        </div>
        <div class="skeleton-action"></div>
      </div>
    </div>

    <!-- Stats Card Skeleton -->
    <div v-else-if="type === 'stats'" class="skeleton-stats">
      <div v-for="stat in count" :key="stat" class="skeleton-stat-card">
        <div class="skeleton-stat-icon"></div>
        <div class="skeleton-stat-content">
          <div class="skeleton-line skeleton-stat-number"></div>
          <div class="skeleton-line skeleton-stat-label"></div>
        </div>
      </div>
    </div>

    <!-- Form Skeleton -->
    <div v-else-if="type === 'form'" class="skeleton-form">
      <div v-for="field in count" :key="field" class="skeleton-form-field">
        <div class="skeleton-line skeleton-label"></div>
        <div class="skeleton-input"></div>
      </div>
    </div>

    <!-- Text Lines Skeleton -->
    <div v-else class="skeleton-text-block">
      <div v-for="line in count" :key="line" class="skeleton-line" :style="{ width: getLineWidth(line) }"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SkeletonLoader',
  props: {
    type: {
      type: String,
      default: 'text',
      validator: value => ['text', 'table', 'card', 'list', 'stats', 'form'].includes(value)
    },
    count: {
      type: Number,
      default: 3
    },
    rows: {
      type: Number,
      default: 5
    },
    columns: {
      type: Number,
      default: 6
    },
    animated: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    getCellWidth(columnIndex) {
      const widths = ['100%', '80%', '90%', '70%', '60%', '85%'];
      return widths[columnIndex % widths.length];
    },
    getLineWidth(lineIndex) {
      const widths = ['100%', '85%', '70%', '90%', '75%'];
      return widths[lineIndex % widths.length];
    }
  }
}
</script>

<style scoped>
.skeleton-loader {
  padding: 20px;
}

/* Base skeleton styles */
.skeleton-line, .skeleton-cell, .skeleton-avatar, .skeleton-circle, .skeleton-input, .skeleton-action, .skeleton-stat-icon {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
}

.skeleton-line, .skeleton-cell, .skeleton-avatar, .skeleton-circle, .skeleton-input, .skeleton-action, .skeleton-stat-icon {
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Dark mode styles */
.dark-mode .skeleton-line, 
.dark-mode .skeleton-cell, 
.dark-mode .skeleton-avatar, 
.dark-mode .skeleton-circle, 
.dark-mode .skeleton-input, 
.dark-mode .skeleton-action, 
.dark-mode .skeleton-stat-icon {
  background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
  background-size: 200% 100%;
}

/* Table skeleton */
.skeleton-table {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.dark-mode .skeleton-table {
  border-color: #404040;
}

.skeleton-table-header, .skeleton-table-row {
  display: flex;
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.dark-mode .skeleton-table-header, 
.dark-mode .skeleton-table-row {
  border-bottom-color: #404040;
}

.skeleton-table-header {
  background-color: #f8f9fa;
}

.dark-mode .skeleton-table-header {
  background-color: #2d2d2d;
}

.skeleton-cell {
  flex: 1;
  margin-right: 14px;
  height: 20px;
}

.skeleton-header-cell {
  height: 24px;
}

.skeleton-cell:last-child {
  margin-right: 0;
}

/* Card skeleton */
.skeleton-card {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.skeleton-card-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.dark-mode .skeleton-card-item {
  border-color: #404040;
  background-color: #2d2d2d;
}

.skeleton-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-content {
  flex: 1;
}

.skeleton-title {
  height: 20px;
  width: 80%;
  margin-bottom: 8px;
}

.skeleton-subtitle {
  height: 14px;
  width: 60%;
  margin-bottom: 8px;
}

.skeleton-text {
  height: 14px;
  margin-bottom: 4px;
}

/* List skeleton */
.skeleton-list-item {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #e9ecef;
  gap: 14px;
}

.dark-mode .skeleton-list-item {
  border-bottom-color: #404040;
}

.skeleton-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-list-title {
  height: 18px;
  width: 200px;
  margin-bottom: 6px;
}

.skeleton-list-subtitle {
  height: 14px;
  width: 150px;
}

.skeleton-action {
  width: 80px;
  height: 32px;
  margin-left: auto;
  border-radius: 6px;
}

/* Stats skeleton */
.skeleton-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.skeleton-stat-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.dark-mode .skeleton-stat-card {
  border-color: #404040;
  background-color: #2d2d2d;
}

.skeleton-stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
}

.skeleton-stat-number {
  height: 24px;
  width: 80px;
  margin-bottom: 8px;
}

.skeleton-stat-label {
  height: 14px;
  width: 120px;
}

/* Form skeleton */
.skeleton-form-field {
  margin-bottom: 20px;
}

.skeleton-label {
  height: 14px;
  width: 120px;
  margin-bottom: 8px;
}

.skeleton-input {
  height: 40px;
  width: 100%;
  border-radius: 6px;
}

/* Text skeleton */
.skeleton-text-block .skeleton-line {
  height: 14px;
  margin-bottom: 8px;
}

.skeleton-text-block .skeleton-line:last-child {
  margin-bottom: 0;
}
</style>